import { useEffect, useState } from "react";

import { App, Select, theme, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  EmailTypeCategories,
  EmailTypes,
  Frequency,
} from "@/shared/utils/constants.js";
import {
  useGetEmailSettingsForUserQuery,
  usePatchEmailSettingsMutation,
} from "@/redux/service.js";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.tsx";
import BaseSwitch from "@/components_v2/FormFields/Switch/BaseSwitch.tsx";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";

const { Title, Text } = Typography;

const EmailSettings = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { notification } = App.useApp();

  const { data: emailSettingsData, isSuccess: isEmailSettingsSuccess } =
    useGetEmailSettingsForUserQuery();
  const [patchEmailSettings] = usePatchEmailSettingsMutation();
  const currentUser = useSelector(selectCurrentUser);

  const [emailSettings, setEmailSettings] = useState(null);
  const [frequency, setFrequency] = useState(null);

  useEffect(() => {
    if (isEmailSettingsSuccess) {
      const digestFrequency = emailSettingsData.find(
        (i) => i.type === EmailTypes.DIGEST.value,
      )?.frequency;
      setFrequency(digestFrequency);
      const settings = emailSettingsData.reduce((acc, i) => {
        acc[i.type] = {
          id: i.id,
          enabled: i.enabled,
        };
        return acc;
      }, {});
      setEmailSettings(settings);
    }
  }, [emailSettingsData, isEmailSettingsSuccess]);

  useEffect(() => {
    if (emailSettings) {
      const digestFrequency = emailSettingsData.find(
        (i) => i.type === EmailTypes.DIGEST.value,
      )?.frequency;
      if (digestFrequency !== frequency) {
        onChangeEmailDigestSetting(
          EmailTypes.DIGEST.value,
          emailSettings[EmailTypes.DIGEST.value]?.enabled,
        );
      }
    }
  }, [frequency]);

  const handleChangeForEmailType = (emailType, value) => {
    const id = emailSettings[emailType].id;
    patchEmailSettings({ id, data: { enabled: value } })
      .unwrap()
      .then(() => {
        notification.success({ message: t("emails.emailSettingsUpdated") });
      });
  };

  const handleDisableAll = (category) => {
    const promises = Object.values(EmailTypes)
      .filter((type) => type.category === category.category)
      .map((type) => type.value)
      .filter((typeValue) => emailSettings[typeValue]?.enabled)
      .map((typeValue) =>
        patchEmailSettings({
          id: emailSettings[typeValue].id,
          data: { enabled: false },
        }),
      );

    Promise.all(promises).then(() => {
      notification.success({ message: t("emails.emailSettingsUpdated") });
    });
  };

  const onChangeEmailDigestSetting = (notificationType, value) => {
    const id = emailSettings[notificationType].id;
    patchEmailSettings({ id, data: { enabled: value, frequency } })
      .unwrap()
      .then(() => {
        notification.success({ message: t("emailSettingsUpdated") });
      });
  };

  if (!currentUser || !emailSettings) return <BaseSpinner />;

  return (
    <div>
      {Object.values(EmailTypeCategories).map((category) => {
        const orderedTypesForCategory = Object.values(EmailTypes)
          .filter((type) => type.category === category.category)
          .map((type) => type.value)
          .filter((typeValue) => emailSettings[typeValue]);

        const areAllDisabled = orderedTypesForCategory.every(
          (setting) => !emailSettings[setting].enabled,
        );

        return (
          <div className="mb-4" key={category.category}>
            <div className="mb-2">
              <Title
                level={4}
                style={{ display: "inline", color: token.colorTextThird }}
                className="mb-2 me-3"
              >
                {t(category.labelKey)}
              </Title>
              <Text
                style={
                  areAllDisabled
                    ? {
                        fontSize: "15px",
                        fontWeight: 500,
                        cursor: "not-allowed",
                      }
                    : {
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "rgba(91, 146, 229, 1)",
                        cursor: "pointer",
                      }
                }
                onClick={
                  areAllDisabled ? null : () => handleDisableAll(category)
                }
              >
                {t("disableAll")}
              </Text>
            </div>

            {orderedTypesForCategory.map((settingType) => (
              <div key={settingType} className="mb-3">
                <BaseSwitch
                  checked={emailSettings[settingType].enabled}
                  onChange={(e) => handleChangeForEmailType(settingType, e)}
                />
                <Text
                  className="ms-2"
                  style={{
                    fontWeight: "bold",
                    color: token.colorTextThird,
                    fontSize: "15px",
                  }}
                >
                  {t(EmailTypes[settingType].labelKey)}
                </Text>
                {category.category === EmailTypeCategories.COMMUNITY.category &&
                  settingType === EmailTypes.DIGEST.value && (
                    <Select
                      style={{ width: "150px" }}
                      defaultValue={Frequency.DAILY.value}
                      className="ms-3"
                      value={frequency}
                      onChange={setFrequency}
                      disabled={!emailSettings[EmailTypes.DIGEST.value].enabled}
                      options={Frequency.asList()}
                    />
                  )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default EmailSettings;
